/* ==========================================================================
   assets/aap-product.js
   Behaviour for sections/aap-product.liquid (Awesome Aminos custom PDP).

   Everything this file needs is already on the page:
     - the <script type="application/json" data-aap-pricing> table
     - data-* settings on <aap-product>
   It never fetches, and it never calls /cart/add.js — the theme's
   ProductForm (is="product-form") owns add-to-cart.
   ========================================================================== */
(function () {
  'use strict';

  var REDUCED_MOTION =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var DAY_MS = 86400000;

  /* ---------------------------------------------------------------- money */
  function formatWithDelimiters(cents, precision, thousands, decimal) {
    thousands = thousands || ',';
    decimal = decimal || '.';
    if (cents === null || cents === undefined || isNaN(cents)) return '0';
    var number = (cents / 100).toFixed(precision);
    var parts = number.split('.');
    var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return whole + (parts[1] ? decimal + parts[1] : '');
  }

  function formatMoney(cents, format) {
    var f = format || '${{amount}}';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = f.match(placeholder);
    var value;
    switch (match && match[1]) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'");
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return f.replace(placeholder, value);
  }

  /* ------------------------------------------------------------- utilities */
  function toInt(value, fallback) {
    var n = parseInt(value, 10);
    return isNaN(n) ? fallback : n;
  }

  /* Figma renders the arrival date as "Wed, May 30th". */
  function ordinal(n) {
    var rem100 = n % 100;
    if (rem100 >= 11 && rem100 <= 13) return n + 'th';
    switch (n % 10) {
      case 1: return n + 'st';
      case 2: return n + 'nd';
      case 3: return n + 'rd';
      default: return n + 'th';
    }
  }

  function setText(el, text) {
    if (el && el.textContent !== text) el.textContent = text;
  }

  function setHidden(el, hidden) {
    if (!el) return;
    if (hidden) el.setAttribute('hidden', '');
    else el.removeAttribute('hidden');
  }

  /* ============================================================= component */
  class AAPProduct extends HTMLElement {
    connectedCallback() {
      if (this._booted) return;
      this._booted = true;

      this.pricing = this._readPricing();
      this.moneyFormat = (this.pricing && this.pricing.moneyFormat) || '${{amount}}';

      this.servings = Math.max(1, toInt(this.dataset.servings, 30));
      this.maxQty = Math.max(1, toInt(this.dataset.maxQuantity, 10));
      this.perDaySuffix = this.dataset.perDaySuffix || '';
      this.ctaLabel = this.dataset.ctaLabel || 'Add to cart';
      this.soldOutLabel = this.dataset.soldoutLabel || 'Sold out';

      this.variantInput = this.querySelector('[data-aap-variant-id]');
      this.planInput = this.querySelector('[data-aap-selling-plan]');
      this.headlinePrice = this.querySelector('[data-aap-headline-price]');
      this.cta = this.querySelector('[data-aap-cta]');
      this.ctaText = this.querySelector('[data-aap-cta-label]') || this.cta;
      this.options = Array.prototype.slice.call(this.querySelectorAll('[data-aap-option]'));

      this._initGallery();
      this._initStickyBridge();
      this._initOptions();
      this._initQuantity();
      this._initDelivery();
      this.render();
    }

    disconnectedCallback() {
      if (this._deliveryTimer) clearInterval(this._deliveryTimer);
    }

    _readPricing() {
      var node = this.querySelector('[data-aap-pricing]');
      if (!node) return null;
      try {
        return JSON.parse(node.textContent);
      } catch (e) {
        return null;
      }
    }

    get variant() {
      if (!this.pricing || !this.pricing.variants || !this.pricing.variants.length) return null;
      var wanted = this.variantInput && this.variantInput.value;
      var found = null;
      if (wanted) {
        found = this.pricing.variants.filter(function (v) {
          return String(v.id) === String(wanted);
        })[0];
      }
      return found || this.pricing.variants[0];
    }

    get quantity() {
      var n = parseInt(this.qtyInput && this.qtyInput.value, 10);
      if (isNaN(n) || n < 1) return 1;
      return Math.min(this.maxQty, n);
    }

    /* ------------------------------------------------------------ gallery */
    _initGallery() {
      this.mainImage = this.querySelector('.aap-media__box img');
      this.track = this.querySelector('[data-aap-thumb-track]');
      this.thumbs = Array.prototype.slice.call(this.querySelectorAll('[data-aap-thumb]'));
      if (!this.mainImage || !this.thumbs.length) return;

      var self = this;
      this.thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          self._showMedia(thumb);
        });
      });

      var behavior = REDUCED_MOTION ? 'auto' : 'smooth';
      var prev = this.querySelector('[data-aap-thumb-nav="prev"]');
      var next = this.querySelector('[data-aap-thumb-nav="next"]');
      var step = function () {
        return self.track ? Math.max(74, self.track.clientWidth * 0.75) : 0;
      };
      if (prev) {
        prev.addEventListener('click', function () {
          self.track.scrollBy({ left: -step(), behavior: behavior });
        });
      }
      if (next) {
        next.addEventListener('click', function () {
          self.track.scrollBy({ left: step(), behavior: behavior });
        });
      }
    }

    _showMedia(thumb) {
      var src = thumb.getAttribute('data-full-src');
      if (!src || !this.mainImage) return;

      /* image_tag emits a srcset, and srcset outranks src. Strip srcset and
         sizes BEFORE assigning src or the browser keeps the old candidate and
         the swap silently does nothing. */
      this.mainImage.removeAttribute('srcset');
      this.mainImage.removeAttribute('sizes');
      this.mainImage.src = src;

      var alt = thumb.getAttribute('data-full-alt');
      if (alt !== null) this.mainImage.alt = alt;

      this.thumbs.forEach(function (t) {
        var on = t === thumb;
        t.classList.toggle('is-active', on);
        if (on) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });
    }

    /* ---------------------------------------------------- purchase options */
    _initOptions() {
      if (!this.options.length) return;
      var self = this;

      this.options.forEach(function (option) {
        option.addEventListener('click', function () {
          self._selectOption(option);
        });
        option.addEventListener('keydown', function (event) {
          if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
            event.preventDefault();
            self._selectOption(option);
            return;
          }
          var keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'];
          if (keys.indexOf(event.key) === -1) return;
          event.preventDefault();
          var usable = self._usableOptions();
          var index = usable.indexOf(option);
          var forward = event.key === 'ArrowRight' || event.key === 'ArrowDown';
          var target = usable[(index + (forward ? 1 : usable.length - 1)) % usable.length];
          if (target) {
            self._selectOption(target);
            target.focus();
          }
        });
      });

      var selected =
        this.options.filter(function (o) {
          return o.classList.contains('is-selected') && !o.hasAttribute('data-aap-disabled');
        })[0] || this._usableOptions()[0];

      if (selected) this._selectOption(selected, true);
    }

    _usableOptions() {
      return this.options.filter(function (o) {
        return !o.hasAttribute('data-aap-disabled') && !o.hasAttribute('hidden');
      });
    }

    _selectOption(option, quiet) {
      if (option.hasAttribute('data-aap-disabled')) return;
      this.options.forEach(function (o) {
        var on = o === option;
        o.classList.toggle('is-selected', on);
        o.setAttribute('aria-checked', on ? 'true' : 'false');
        o.tabIndex = on ? 0 : -1;
      });
      this.selectedOption = option;
      this._syncSellingPlan();
      if (!quiet) this.render();
    }

    _syncSellingPlan() {
      if (!this.planInput) return;
      var plan =
        (this.selectedOption && this.selectedOption.getAttribute('data-selling-plan')) || '';
      this.planInput.value = plan;
      /* One-time: the field is empty and disabled, so no selling_plan param is
         submitted at all. Subscribe: the plan id goes with the form. */
      this.planInput.disabled = plan === '';
    }

    /* ----------------------------------------------------------- quantity */
    _initQuantity() {
      this.qtyInput = this.querySelector('[data-aap-qty-input]');
      this.qtyMinus = this.querySelector('[data-aap-qty="minus"]');
      this.qtyPlus = this.querySelector('[data-aap-qty="plus"]');
      if (!this.qtyInput) return;

      var self = this;
      this.qtyInput.setAttribute('max', String(this.maxQty));

      var bump = function (delta) {
        self.qtyInput.value = String(self._clamp(self.quantity + delta));
        self.render();
      };
      if (this.qtyMinus) {
        this.qtyMinus.addEventListener('click', function () {
          bump(-1);
        });
      }
      if (this.qtyPlus) {
        this.qtyPlus.addEventListener('click', function () {
          bump(1);
        });
      }

      /* Clamp on change, not on input: clearing the field to retype must not
         snap back to 1 mid-edit. */
      this.qtyInput.addEventListener('change', function () {
        self.qtyInput.value = String(self._clamp(parseInt(self.qtyInput.value, 10)));
        self.render();
      });
      this.qtyInput.addEventListener('input', function () {
        self.render();
      });
    }

    _clamp(n) {
      if (isNaN(n) || !isFinite(n)) return 1;
      return Math.min(this.maxQty, Math.max(1, Math.floor(n)));
    }

    /* -------------------------------------------------------------- render */
    render() {
      var variant = this.variant;
      if (!variant) return;
      var qty = this.quantity;
      var self = this;

      if (this.variantInput) this.variantInput.value = variant.id;

      var allocations = variant.selling_plan_allocations || [];

      this.options.forEach(function (option) {
        var planId = option.getAttribute('data-selling-plan') || '';
        var price;
        var compare;

        if (planId) {
          var allocation = allocations.filter(function (a) {
            return String(a.selling_plan_id) === String(planId);
          })[0];

          /* No allocation for this variant: hide the subscribe card rather
             than render a broken one. */
          if (!allocation) {
            option.setAttribute('hidden', '');
            option.setAttribute('data-aap-disabled', '');
            if (option === self.selectedOption) {
              var fallback = self._usableOptions()[0];
              if (fallback) self._selectOption(fallback, true);
            }
            return;
          }
          option.removeAttribute('hidden');
          option.removeAttribute('data-aap-disabled');
          price = allocation.price;
          compare = allocation.compare_at_price;
        } else {
          price = variant.price;
          compare = variant.compare_at_price;
        }

        var hasDiscount = typeof compare === 'number' && compare > price;
        var savePct = hasDiscount ? Math.round(((compare - price) * 100) / compare) : 0;

        /* Card prices scale with quantity. */
        setText(
          option.querySelector('[data-aap-option-price]'),
          formatMoney(price * qty, self.moneyFormat)
        );

        var compareEl = option.querySelector('[data-aap-option-compare]');
        if (compareEl) {
          if (hasDiscount) setText(compareEl, formatMoney(compare * qty, self.moneyFormat));
          setHidden(compareEl, !hasDiscount);
        }

        /* The percentage is always computed, never configured. Try Once shows
           "0% OFF", so this renders at zero rather than hiding. */
        var offerEl = option.querySelector('[data-aap-option-offer]');
        var template = option.getAttribute('data-offer-template');
        if (offerEl && template) setText(offerEl, template.replace('[save]', savePct));

        /* Per-day is per bag. Two bags do not change what one day costs, so
           this never multiplies by quantity. */
        setText(
          option.querySelector('[data-aap-option-perday]'),
          formatMoney(Math.round(price / self.servings), self.moneyFormat) + self.perDaySuffix
        );
      });

      if (this.headlinePrice) {
        setText(this.headlinePrice, formatMoney(variant.price * qty, this.moneyFormat));
      }

      if (this.qtyMinus) this.qtyMinus.disabled = qty <= 1;
      if (this.qtyPlus) this.qtyPlus.disabled = qty >= this.maxQty;

      this._syncSticky(variant, allocations);

      if (this.cta) {
        this.cta.disabled = !variant.available;
        setText(this.ctaText, variant.available ? this.ctaLabel : this.soldOutLabel);
      }
    }

    /* ------------------------------------------------------- sticky ATC bar */
    /* The sticky bar is rendered from layout/theme.liquid for every product
       template and carries its OWN add-to-cart form, which this section has to keep
       in step with the option the shopper actually picked.

       Two things fight over that form's hidden selling_plan input:
         - sticky-atc-bar.liquid:73-78 renders it from `selling_plan_allocations |
           first`, so the MARKUP always says subscription; and
         - sticky-atc-bar.js seeds itself at construction from
           [data-aa-pdp-sub-picker] (the control template's picker, which this
           section does not render), resolves an empty plan, and DELETES the input
           (:189-190).
       The script wins, so the shipped behaviour was always one-time. Inspecting the
       rendered HTML does not show this — the input is there until JS removes it. */
    _initStickyBridge() {
      var self = this;
      var tries = 0;
      var find = function () {
        var bar = document.querySelector('sticky-atc-bar') || document.querySelector('.sticky-atc-bar');
        if (bar) {
          self.sticky = {
            bar: bar,
            label: bar.querySelector('[data-aa-sticky-purchase-label]'),
            price: bar.querySelector('[data-aa-sticky-selected-price]'),
            compare: bar.querySelector('[data-aa-sticky-selected-compare]')
          };
          self.render();
          /* The element may be in the DOM but not yet upgraded, in which case the
             sync below had no listener and its constructor has yet to run the seed
             that wipes the plan input. Re-run once both have certainly happened. */
          setTimeout(function () { self.render(); }, 0);
          if (window.customElements && customElements.whenDefined) {
            customElements.whenDefined('sticky-atc-bar').then(function () { self.render(); });
          }
          return true;
        }
        return false;
      };
      if (find()) return;
      /* The sticky bar is a separate section and may parse after this one. */
      var poll = setInterval(function () {
        if (find() || ++tries > 20) clearInterval(poll);
      }, 250);
    }

    _syncSticky(variant, allocations) {
      if (!this.sticky || !this.selectedOption) return;

      var planId = this.selectedOption.getAttribute('data-selling-plan') || '';
      var price = variant.price;
      var compare = variant.compare_at_price;

      if (planId) {
        var allocation = (allocations || []).filter(function (a) {
          return String(a.selling_plan_id) === String(planId);
        })[0];
        if (allocation) {
          price = allocation.price;
          compare = allocation.compare_at_price;
        }
      }

      /* sticky-atc-bar.js OWNS that form, and it deletes the hidden selling_plan
         input: its constructor seeds itself from [data-aa-pdp-sub-picker] — the
         control template's picker, absent here — resolves an empty plan, and calls
         syncPurchaseOption, which removes the input (sticky-atc-bar.js:189-190).
         Writing to a cached node therefore wrote to a node that was no longer in the
         form, and every sticky add went through as a ONE-TIME purchase while this
         section kept displaying "Subscribe & save".

         So speak its contract instead of poking its DOM: this is the same event
         aminos-pdp-subscription-picker.js dispatches on the control template
         (:113-126), and its handler RE-CREATES the input when a plan is passed
         (:180-188). Dispatch is synchronous, so the label write below still wins. */
      document.dispatchEvent(
        new CustomEvent('aa:pdp-purchase-option:changed', {
          bubbles: true,
          detail: {
            mode: planId ? 'subscription' : 'one_time',
            sellingPlanId: planId,
            /* 1, not this.quantity — the bar adds a single unit and its price line is
               a unit price. This is what the bar's own seed passes. */
            quantity: 1,
            unitPriceCents: price,
            unitCompareCents: typeof compare === 'number' ? compare : 0,
            totalPriceCents: price,
            totalCompareCents: typeof compare === 'number' ? compare : 0,
            price: formatMoney(price, this.moneyFormat),
            comparePrice:
              typeof compare === 'number' && compare > price ? formatMoney(compare, this.moneyFormat) : ''
          }
        })
      );

      /* Fallback for the case where sticky-atc-bar.js never upgraded the element, so
         nothing was listening and the input still holds the Liquid default (always
         the first allocation, i.e. always a subscription). Re-queried every time,
         never cached, because the bar removes and re-creates this node. */
      var planInput = this.sticky.bar && this.sticky.bar.querySelector('[data-aa-sticky-plan-input]');
      if (planInput) {
        planInput.value = planId;
        planInput.disabled = planId === '';
      }

      var title = this.selectedOption.querySelector('.aap-option__title');
      if (this.sticky.label && title) setText(this.sticky.label, title.textContent.trim());

      /* The sticky bar submits its own quantity (or none, which Shopify reads as
         1), so its price stays a unit price and must not use this.quantity. */
      if (this.sticky.price) setText(this.sticky.price, formatMoney(price, this.moneyFormat));

      if (this.sticky.compare) {
        var show = typeof compare === 'number' && compare > price;
        if (show) setText(this.sticky.compare, formatMoney(compare, this.moneyFormat));
        setHidden(this.sticky.compare, !show);
      }
    }

    /* ------------------------------------------------------------ delivery */
    _initDelivery() {
      this.deliveryDate = this.querySelector('[data-aap-delivery-date]');
      this.deliveryCountdown = this.querySelector('[data-aap-delivery-countdown]');
      if (!this.deliveryDate && !this.deliveryCountdown) return;

      this.timezone = this.dataset.timezone || 'America/New_York';
      this.cutoffHour = Math.min(23, Math.max(0, toInt(this.dataset.cutoffHour, 14)));
      this.cutoffMinute = Math.min(59, Math.max(0, toInt(this.dataset.cutoffMinute, 0)));
      this.processingDays = Math.max(0, toInt(this.dataset.processingDays, 1));
      this.transitDays = Math.max(0, toInt(this.dataset.transitDays, 3));

      var self = this;
      this._tickDelivery();
      this._deliveryTimer = setInterval(function () {
        self._tickDelivery();
      }, 1000);
    }

    /* Wall-clock parts in the shop's timezone. */
    _zoneParts(date) {
      try {
        var formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: this.timezone,
          hourCycle: 'h23',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });
        var out = {};
        formatter.formatToParts(date).forEach(function (part) {
          if (part.type !== 'literal') out[part.type] = part.value;
        });
        return {
          y: +out.year,
          m: +out.month,
          d: +out.day,
          h: +out.hour % 24,
          mi: +out.minute,
          s: +out.second
        };
      } catch (e) {
        return {
          y: date.getFullYear(),
          m: date.getMonth() + 1,
          d: date.getDate(),
          h: date.getHours(),
          mi: date.getMinutes(),
          s: date.getSeconds()
        };
      }
    }

    _isBusinessDay(utcMs) {
      var day = new Date(utcMs).getUTCDay();
      return day !== 0 && day !== 6;
    }

    _addBusinessDays(utcMs, count) {
      var out = utcMs;
      var remaining = Math.max(0, count);
      while (remaining > 0) {
        out += DAY_MS;
        if (this._isBusinessDay(out)) remaining -= 1;
      }
      while (!this._isBusinessDay(out)) out += DAY_MS;
      return out;
    }

    _tickDelivery() {
      var parts = this._zoneParts(new Date());

      /* Anchor the shop's wall clock to UTC so day arithmetic never trips
         over a DST change. */
      var wallNow = Date.UTC(parts.y, parts.m - 1, parts.d, parts.h, parts.mi, parts.s);
      var orderDay = Date.UTC(parts.y, parts.m - 1, parts.d);
      var cutoffOffset = (this.cutoffHour * 3600 + this.cutoffMinute * 60) * 1000;
      var cutoff = orderDay + cutoffOffset;

      if (wallNow >= cutoff || !this._isBusinessDay(orderDay)) {
        do {
          orderDay += DAY_MS;
        } while (!this._isBusinessDay(orderDay));
        cutoff = orderDay + cutoffOffset;
      }

      var ship = this._addBusinessDays(orderDay, this.processingDays);
      var arrive = this._addBusinessDays(ship, this.transitDays);

      if (this.deliveryDate) {
        var label;
        try {
          var d = new Date(arrive);
          var parts = new Intl.DateTimeFormat('en-US', {
            timeZone: 'UTC', weekday: 'short', month: 'long', day: 'numeric'
          }).formatToParts(d);
          var out = {};
          parts.forEach(function (p) { out[p.type] = p.value; });
          label = out.weekday + ', ' + out.month + ' ' + ordinal(+out.day);
        } catch (e) {
          label = new Date(arrive).toDateString();
        }
        setText(this.deliveryDate, label);
      }

      if (this.deliveryCountdown) {
        var totalMinutes = Math.max(0, Math.floor((cutoff - wallNow) / 60000));
        var days = Math.floor(totalMinutes / 1440);
        var hours = Math.floor((totalMinutes % 1440) / 60);
        var minutes = totalMinutes % 60;
        var text = days > 0
          ? days + (days === 1 ? ' day ' : ' days ') + hours + (hours === 1 ? ' hr' : ' hrs')
          : hours + (hours === 1 ? ' hr ' : ' hrs ') + minutes + (minutes === 1 ? ' min' : ' mins');
        setText(this.deliveryCountdown, text);
      }
    }
  }

  if (!customElements.get('aap-product')) {
    customElements.define('aap-product', AAPProduct);
  }
})();